/**
 * Episode + progress + entitlement load — port of AppRootView.loadEpisode() without the
 * debug flags: loadEpisode(id, locale, automatic ? 'english' : 'strict') → loadProgress
 * (else a fresh progress) → isPremium(user) mirrored into progress.isPremium.
 * A cold start on an unpublished selected case falls back to EP1 (MissingPublishedEpisodeError).
 */
import { DEFAULT_EPISODE_ID, isUntouchedProgress, migrateRejectedProofReplyReferences, newProgress, progressFromRow, type EpisodeContent, type EpisodeProgress, type ProgressRow } from '@/domain';
import { MissingPublishedEpisodeError, type LoadedEpisode, type LocaleFallbackPolicy } from '@/services/episodeRepository';

export interface EpisodeSessionDeps {
  loadEpisode(id: string, locale: string, policy: LocaleFallbackPolicy): Promise<LoadedEpisode>;
  loadProgress(userID: string, episode: EpisodeContent): Promise<EpisodeProgress | undefined>;
  /** Row-only fetch: when present it runs in parallel with `loadEpisode`, mapped with `progressFromRow` once the episode arrives. */
  loadProgressRow?(userID: string, episodeID: string): Promise<ProgressRow | undefined>;
  isPremium(userID: string): Promise<boolean>;
}

export interface LoadedEpisodeSession {
  episode: EpisodeContent;
  servedLocale: string;
  /** The locale that was requested (the effective UI locale at load time). */
  requestedLocale: string;
  progress: EpisodeProgress;
  isPremium: boolean;
  /** Nothing done yet (drives `episode_started` and the cold open to chat). */
  untouched: boolean;
}

export function fallbackPolicy(selection: string): LocaleFallbackPolicy {
  return selection === 'automatic' ? 'english' : 'strict';
}

export async function loadEpisodeSession(
  input: { episodeID: string; locale: string; selection: string; userID: string; allowDefaultFallback: boolean; premiumHint?: boolean },
  deps: EpisodeSessionDeps,
): Promise<LoadedEpisodeSession> {
  // The entitlement does not depend on the episode: fetched in parallel (one round trip less on the splash).
  const premiumRequest = deps.isPremium(input.userID).then((v): boolean | undefined => v, () => undefined);
  // Same for the progress row (only the mapping needs the content).
  const rowRequest = deps.loadProgressRow?.(input.userID, input.episodeID).then((row) => ({ row }), (error: unknown) => ({ error }));
  let loaded: LoadedEpisode;
  try {
    loaded = await deps.loadEpisode(input.episodeID, input.locale, fallbackPolicy(input.selection));
  } catch (error) {
    if (input.allowDefaultFallback && input.episodeID !== DEFAULT_EPISODE_ID && error instanceof MissingPublishedEpisodeError) {
      return loadEpisodeSession({ ...input, episodeID: DEFAULT_EPISODE_ID, allowDefaultFallback: false }, deps);
    }
    throw error;
  }
  const episode = loaded.content;
  let progress: EpisodeProgress;
  try {
    if (rowRequest) {
      const result = await rowRequest;
      if ('error' in result) throw result.error;
      progress = result.row ? progressFromRow(result.row, episode) : newProgress();
    } else {
      progress = (await deps.loadProgress(input.userID, episode)) ?? newProgress();
    }
  } catch {
    progress = newProgress();
  }
  // Offline: keep the hint.
  const premium = (await premiumRequest) ?? input.premiumHint ?? false;
  progress = migrateRejectedProofReplyReferences({ ...progress, isPremium: premium }, episode);
  return { episode, servedLocale: loaded.servedLocale, requestedLocale: input.locale, progress, isPremium: premium, untouched: isUntouchedProgress(progress) };
}
